import React from 'react';
import { Link } from 'react-router-dom';
import CompanyDetailsBlock from '../components/CompanyDetailsBlock';

const Section = ({ title, children }) => (
  <section>
    <h2
      className="text-xl font-semibold text-[#112318] mb-3"
      style={{ fontFamily: 'Playfair Display, serif' }}
    >
      {title}
    </h2>
    <div className="leading-relaxed space-y-3">{children}</div>
  </section>
);

const PayoutPolicyPage = () => (
  <div className="min-h-screen bg-[#FAFAF7]">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="payout-policy-page">
      <h1
        className="text-4xl sm:text-5xl tracking-tight leading-none font-bold text-[#112318] mb-4"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        Payout Policy
      </h1>
      <p className="text-sm text-[#4A5D50] mb-12">Effective Date: May 2026</p>

      <div className="prose prose-sm max-w-none space-y-10 text-[#4A5D50]">
        <Section title="1. How Ticket Funds Are Collected">
          <p>
            All ticket sales on VinePass are processed through PayFast, our PCI-DSS compliant
            payment gateway. Funds are collected in South African Rand (ZAR) and held by VinePass
            on behalf of the organiser until they become eligible for payout.
          </p>
        </Section>

        <Section title="2. Organiser Balance">
          <p>
            Your dashboard displays a live balance reflecting completed ticket sales for past
            events, net of VinePass fees you have chosen to absorb, refunds and any held reserves.
            Only sales for events that have already taken place become withdrawable.
          </p>
        </Section>

        <Section title="3. Withdrawal Request Process">
          <p>
            To request a payout, navigate to your organiser dashboard, ensure your banking details
            are saved, and click <em>Request Withdrawal</em>. Requests are placed in a{' '}
            <strong>Pending Review</strong> queue and reviewed manually by the VinePass team.
          </p>
        </Section>

        <Section title="4. Required Organiser Banking Details">
          <p>
            We require: account holder name, bank name, branch code, account number, and account
            type (cheque / savings / transmission / business). Account numbers are encrypted at
            rest. Bank details may only be added or edited by the account owner — associates
            cannot view or modify banking information.
          </p>
        </Section>

        <Section title="5. Payout Review and Approval">
          <p>
            VinePass reviews each request to verify that the underlying ticket orders are
            completed, that no chargebacks or refund disputes are open, and that the requested
            amount does not exceed the withdrawable balance. The review window is typically 1–3
            business days.
          </p>
        </Section>

        <Section title="6. Deductions">
          <p>
            Net payout = gross ticket sales − VinePass curation fees − approved refunds −
            chargebacks − any active reserve holds. Where the organiser elected the
            customer-pays fee strategy, VinePass fees are passed to the buyer at checkout and do
            not reduce the payout.
          </p>
        </Section>

        <Section title="7. Payout Timeframe">
          <p>
            Once approved, payouts are processed via EFT to the banking details on file.
            Settlement to the organiser&apos;s bank typically arrives within 2–5 business days
            depending on banks, weekends and South African public holidays.
          </p>
        </Section>

        <Section title="8. Payout Email Notifications">
          <p>
            VinePass admin is notified at <a href="mailto:info@vinepass.co.za" className="text-[#1F3D2B] underline">info@vinepass.co.za</a> when an
            organiser requests a withdrawal, with the full breakdown attached. Organisers receive
            an email confirmation when the request is received and again when the payout has been
            approved and paid.
          </p>
        </Section>

        <Section title="9. Disputes">
          <p>
            For payout queries, email{' '}
            <a href="mailto:info@vinepass.co.za" className="text-[#1F3D2B] underline">info@vinepass.co.za</a>{' '}
            with your payout request ID. We aim to respond within two business days.
          </p>
        </Section>

        <CompanyDetailsBlock />
      </div>

      <div className="mt-16 pt-8 border-t border-[#E5E9E6] flex items-center justify-between">
        <Link to="/organiser-terms" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="payout-back-organiser">
          &larr; Organiser Terms
        </Link>
        <Link to="/refund-policy" className="text-[#1F3D2B] font-semibold hover:underline text-sm" data-testid="payout-to-refund">
          Refund Policy &rarr;
        </Link>
      </div>
    </div>
  </div>
);

export default PayoutPolicyPage;
